import { randomUUID } from 'node:crypto';
import { isType, match } from 'variant';
import { CliError, CliErrorCode } from '@wix/cli-error';
import { WIX_CONFIG_FILENAME } from '@wix/cli-core-definitions';
import { replaceExtension } from '../dev-center/components/path.js';
import type {
  BackendComponent,
  VirtualComponent,
} from '../dev-center/components/dev-center-component.js';
import { DevCenterComponent } from '../dev-center/components/dev-center-component.js';

type ComponentsWithConfig = Exclude<
  DevCenterComponent,
  BackendComponent | VirtualComponent
>;

export const validateComponents = (components: DevCenterComponent[]) => {
  const devCenterComponentsToIdMap = new Map<string, ComponentsWithConfig[]>();

  const componentsWithConfig = components.filter(
    (component): component is ComponentsWithConfig =>
      !isType(component, DevCenterComponent.Api) &&
      !isType(component, DevCenterComponent.WebMethod) &&
      !isType(component, DevCenterComponent.Event) &&
      !isType(component, DevCenterComponent.ServicePlugin) &&
      !isType(component, DevCenterComponent.BlocksMainComponent) &&
      !isType(component, DevCenterComponent.Topology) &&
      !isType(component, DevCenterComponent.Monitoring)
  );

  const sortedComponentsById = [...componentsWithConfig].sort((a, b) =>
    a.id.localeCompare(b.id)
  );
  for (const component of sortedComponentsById) {
    const componentList = devCenterComponentsToIdMap.get(component.id) ?? [];
    componentList.push(component);
    devCenterComponentsToIdMap.set(component.id, componentList);
  }

  const devCenterComponentsWithSameIDMap = new Map<
    string,
    ComponentsWithConfig[]
  >();

  for (const [id, components] of devCenterComponentsToIdMap.entries()) {
    if (components.length > 1) {
      devCenterComponentsWithSameIDMap.set(id, components);
    }
  }

  if (devCenterComponentsWithSameIDMap.size) {
    throw new CliError({
      code: CliErrorCode.DuplicateComponentIdsError({
        issues: Array.from(devCenterComponentsWithSameIDMap.entries()).map(
          ([id, devCenterComponents]) => {
            return {
              id,
              components: devCenterComponents
                .map((component) =>
                  match(component, {
                    DashboardPage: ({ title, relativePath }) => ({
                      name: title,
                      type: 'page' as const,
                      path: replaceExtension(relativePath, 'json'),
                    }),
                    DashboardPlugin: ({ title, relativePath }) => ({
                      name: title,
                      type: 'plugin' as const,
                      path: replaceExtension(relativePath, 'json'),
                    }),
                    DashboardMenuPlugin: ({ title, relativePath }) => ({
                      name: title,
                      type: 'menu-plugin' as const,
                      // We can use the relative path since the path already points to the menu config json
                      path: relativePath,
                    }),
                    DashboardModal: ({ title, relativePath }) => ({
                      name: title,
                      type: 'modal' as const,
                      path: replaceExtension(relativePath, 'json'),
                    }),
                    DashboardSidebarCategory: ({ label }) => ({
                      name: label,
                      type: 'sidebar-category' as const,
                      path: WIX_CONFIG_FILENAME,
                    }),
                    EmbeddedScript: ({ relativePath }) => ({
                      name: relativePath,
                      type: 'embedded-script' as const,
                      path: replaceExtension(relativePath, 'json'),
                    }),
                    CustomElement: ({ name, relativePath }) => ({
                      name,
                      type: 'custom-element' as const,
                      path: replaceExtension(relativePath, 'json'),
                    }),
                    BlocksWidget: ({ relativePath }) => ({
                      name: '',
                      type: 'blocks-widget' as const,
                      path: replaceExtension(relativePath, 'json'),
                    }),
                    BlocksWidgetPanel: ({ relativePath }) => ({
                      name: '',
                      type: 'blocks-widget-panel' as const,
                      path: replaceExtension(relativePath, 'json'),
                    }),
                    SitePlugin: ({ marketData, relativePath }) => ({
                      name: marketData.name,
                      type: 'site-plugin' as const,
                      path: replaceExtension(relativePath, 'json'),
                    }),
                    SiteComponent: ({ relativePath }) => ({
                      name: relativePath,
                      type: 'site-component' as const,
                      path: replaceExtension(relativePath, 'json'),
                    }),
                    SiteComponentPanel: ({ relativePath }) => ({
                      name: relativePath,
                      type: 'site-component-panel' as const,
                      path: replaceExtension(relativePath, 'json'),
                    }),
                  })
                )
                .sort((a, b) => a.name.localeCompare(b.name)),
              suggestedId: randomUUID(),
            };
          }
        ),
      }),
      cause: null,
    });
  }
};
